import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import { SUPPORTED_LANGUAGES } from "@/i18n/translations";
import { accountNav, isGateLocked } from "./AccountShell";

/**
 * Full-screen editorial mobile menu with big serif nav, staggered fade-in,
 * language switcher and session CTAs. Rendered by VangardHeader when the
 * burger is toggled.
 */
export default function MobileMenu({ open, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useTranslation();

  // Lock page scroll while the menu is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const nav = [
    { to: "/", label: t("vc.nav.explore") || "Explore", index: "01" },
    { to: "/about", label: t("vc.nav.about"), index: "02" },
    { to: "/membership", label: t("vc.nav.membership"), index: "03" },
    { to: "/magazine", label: t("vc.nav.magazine"), index: "04" },
    { to: "/concierge", label: t("vc.nav.concierge"), index: "05" },
  ];

  const legal = [
    { to: "/privacy", label: t("menu.privacy") },
    { to: "/terms", label: t("menu.terms") },
  ];

  const parent = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.08 } },
    exit: { opacity: 0, transition: { staggerChildren: 0.03, staggerDirection: -1 } },
  };
  const child = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, y: 15, transition: { duration: 0.25 } },
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[60] h-[100dvh] bg-[#050505] flex flex-col overflow-hidden"
          data-testid="mobile-menu"
        >
          {/* Subtle V watermark for editorial feel */}
          <svg
            viewBox="0 0 200 240"
            className="absolute -top-10 -right-14 w-[380px] h-[460px] pointer-events-none select-none text-white/[0.05]"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0 0h40l60 180L160 0h40L115 240H85L0 0z" />
          </svg>

          {/* Spacer to sit below the fixed header (68px) */}
          <div className="h-[56px] flex-shrink-0" />

          {/* Nav */}
          <motion.nav
            variants={parent}
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative px-6 pt-4 pb-4 flex-1 min-h-0 overflow-y-auto no-scrollbar flex flex-col justify-center"
          >
            <motion.div variants={child} className="text-[10px] tracking-[0.4em] uppercase text-neutral-500 mb-2">
              {t("vc.footer.stamp")}
            </motion.div>

            {nav.map((n) => {
              const active = location.pathname === n.to;
              return (
                <motion.div key={n.to} variants={child}>
                  <Link
                    to={n.to}
                    onClick={onClose}
                    className={`group flex items-baseline justify-between ${user ? "py-1.5" : "py-2.5"} border-b border-white/[0.06] transition-colors ${
                      active ? "text-white" : "text-neutral-300 hover:text-white"
                    }`}
                    data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                  >
                    <div className="flex items-baseline gap-4">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 w-8">{n.index}</span>
                      <span className={`font-serif leading-[1.1] tracking-tight ${user ? "text-[21px]" : "text-[26px]"}`}>{n.label}</span>
                    </div>
                    <ArrowUpRight
                      size={16}
                      className="text-neutral-600 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                    />
                  </Link>
                </motion.div>
              );
            })}

            {/* Members section */}
            {user && (
              <motion.div variants={child} className="mt-4">
                <div className="text-[10px] tracking-[0.4em] uppercase text-neutral-500">
                  {t("vc.acct.members-area")}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {accountNav(t).map((n) => {
                    const locked = isGateLocked(n, user);
                    return (
                      <Link
                        key={n.to}
                        to={n.to}
                        onClick={onClose}
                        data-testid={`mobile-acct-${n.to.replace(/\//g, "-").slice(1)}`}
                        className="flex items-center justify-between gap-2 rounded-xl border border-white/[0.08] px-2.5 py-1.5 text-neutral-300 hover:text-white hover:border-white/25 transition-colors"
                      >
                        <span className="text-[9px] tracking-[0.15em] uppercase truncate">{n.label}</span>
                        {locked ? <Lock size={11} className="text-neutral-600" /> : <n.icon size={14} strokeWidth={1.6} className="text-neutral-500" />}
                      </Link>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Session block */}
            <motion.div variants={child} className="mt-4 flex flex-col gap-2">
              {user ? (
                <>
                  <button
                    onClick={() => { onClose(); navigate("/account"); }}
                    className="w-full inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-2.5 rounded-full text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
                    data-testid="mobile-account-cta"
                  >
                    {t("vc.nav.account")}
                  </button>
                  <button
                    onClick={() => { onClose(); logout(); navigate("/"); }}
                    className="w-full inline-flex items-center justify-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full text-[11px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                    data-testid="mobile-logout"
                  >
                    {t("menu.logout")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { onClose(); navigate("/register"); }}
                    className="w-full inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3 rounded-full text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
                    data-testid="mobile-join"
                  >
                    {t("vc.nav.join")}
                  </button>
                  <button
                    onClick={() => { onClose(); navigate("/login"); }}
                    className="w-full inline-flex items-center justify-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full text-[11px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                    data-testid="mobile-signin"
                  >
                    {t("vc.nav.signin")}
                  </button>
                </>
              )}
            </motion.div>
          </motion.nav>

          {/* Footer strip */}
          <motion.div
            variants={child}
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative flex-shrink-0 border-t border-white/[0.06] px-6 py-3 flex items-center justify-between"
          >
            <div className="flex gap-4 text-[10px] tracking-[0.3em] uppercase text-neutral-500">
              {legal.map((l) => (
                <Link key={l.to} to={l.to} onClick={onClose} className="hover:text-white transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {SUPPORTED_LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`text-[10px] tracking-[0.3em] uppercase px-2 py-1 rounded-full transition-colors ${
                    lang === l.code
                      ? "bg-white text-black"
                      : "text-neutral-400 hover:text-white"
                  }`}
                  data-testid={`mobile-lang-${l.code}`}
                >
                  {l.short}
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
